import { logger } from '@app/logger';
import { getTgWebAppPlatform } from '@app/logger/crashReporter';

const FALLBACK_ANIMATION = 'splash-fallback-lift';

/**
 * The free Sentry plan stops taking errors at 5k a month, and a bug that stalls
 * every session would spend that within days. `report` still gets every late
 * splash: it goes to our own /client-errors, which has no such quota.
 */
const SENTRY_SAMPLE_RATE = 0.25;

/**
 * index.html lifts its splash on a 2 s timer, and a flood of earlier timers can
 * hold that one back: a render loop on the sign-up page once kept new Android
 * users on the splash for about 30 s, with every tap landing on it. Nothing
 * throws when that happens, so nothing would report it.
 *
 * This starts index.html's CSS fallback, which needs no timer and runs a second
 * later, reports each splash that needed it, and times every splash in Sentry
 * (span op `ui.splash`). A healthy page has removed the splash before the
 * fallback runs, so it reports nothing. The span is sampled like the rest of the
 * tracing, so it adds one small request to Sentry in those sessions only.
 */
export const watchSplash = (
  report: (error: unknown) => void,
  random: () => number = Math.random,
) => {
  document.documentElement.classList.add('app-started');

  const splash = document.getElementById('preloader');
  const splashParent = splash?.parentNode;

  if (!splash || !splashParent) return;

  const span = logger.startInactiveSpan({
    name: 'splash',
    op: 'ui.splash',
    startTime: performance.timeOrigin / 1000,
    // Its own transaction: the router's first redirect ends the pageload span
    // about a second in, which would cancel a child before the splash lifts.
    forceTransaction: true,
  });
  let liftedBy = 'timer';

  const observer = new MutationObserver(() => {
    if (splash.isConnected) return;

    observer.disconnect();
    span.setAttribute('splash.lifted_by', liftedBy);
    span.end();
  });
  observer.observe(splashParent, { childList: true });

  splash.addEventListener('animationend', (event) => {
    // The progress bar's own animation bubbles up here too.
    if (event.animationName !== FALLBACK_ANIMATION) return;

    liftedBy = 'fallback';
    splash.remove();

    const late = new Error('Splash timer late');
    late.name = 'SplashTimerLate';
    report(late);

    if (random() >= SENTRY_SAMPLE_RATE) return;

    // An error, not a message: with attachStacktrace on, Sentry titles a message
    // after the minified function that sent it.
    logger.captureException(late, {
      level: 'warning',
      tags: {
        section: 'splash',
        tg_platform: getTgWebAppPlatform() ?? 'unknown',
      },
      extra: {
        path: window.location.pathname,
        visibility: document.visibilityState,
        has_focus: document.hasFocus(),
        since_load_ms: Math.round(performance.now()),
      },
    });
  });
};
